'use client';

import cx from 'classix';
import { useAtomValue } from 'jotai/index';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import {
  type MouseEvent,
  useState,
} from 'react';
import { FaHeart } from 'react-icons/fa6';

import ListingAddress from '@/features/listing/components/listing-card/listing-address/listing-address';
import { type ListingCardViewBaseProps } from '@/features/listing/components/listing-card/listing-card-types';
import ListingRate from '@/features/listing/components/listing-card/listing-rate/listing-rate';
import createListingBookmark from '@/features/listing/requests/create-listing-bookmark';
import deleteListingBookmark from '@/features/listing/requests/delete-listing-bookmark';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';
import { VerificationStatus } from '@/features/user/types/user-id-verification-types';
import {
  Link,
  useRouter,
} from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import Avatar from '@/ui/avatar/avatar';
import Badge from '@/ui/badge/badge';

import styles from './listing-card-view-list.module.scss';

export default function ListingCardViewList(props: ListingCardViewBaseProps) {
  const {
    isListingLinkDisabled = false,
    isListingPerson,
    link = null,
    listing,
    showBookmarkButton,
    showCheckStatus,
    showListingUser,
    showMissingData,
  } = props;

  const router = useRouter();
  const appFlyerMobileBookmarkLink = useAppFlyerLink('mobile', 'mobileListingBookmark', ROUTER_LINKS.phoneAuth);

  const translatesListingCard = useTranslations('listing.card');
  const translatesVerifications = useTranslations('global.verifications');

  const loggedUser = useAtomValue(loggedUserAtom);

  const listingStatusToBadgeColor = {
    [VerificationStatus.New]: 'gray',
    [VerificationStatus.Passed]: 'green',
    [VerificationStatus.Pending]: 'orange',
    [VerificationStatus.Rejected]: 'red',
    [VerificationStatus.Error]: 'red',
  } as const;

  const [isListingBookmarked,
    setIsListingBookmarked] = useState<boolean>(listing.isBookmarked);

  const handleBookmarkClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (loggedUser) {
      if (isListingBookmarked) {
        setIsListingBookmarked(false);
        deleteListingBookmark(listing.id, listing.listingTypeId)
          .catch(() => null);
      } else {
        setIsListingBookmarked(true);
        createListingBookmark(listing.id, listing.listingTypeId)
          .catch(() => null);
      }
    } else {
      router.push(appFlyerMobileBookmarkLink);
    }
  };

  const renderListingBody = () => (
    <>
      <div className={styles.listingCardViewList_imageContainer}>
        <Image
          className={cx(styles.listingCardViewList_image)}
          src={listing.images[0].pathSmall.toString()}
          alt={listing.title}
          fill
          unoptimized
        />
      </div>

      <div
        className={styles.listingCardViewList_info}
      >
        <h2 className={styles.listingCardViewList_title}>
          {listing.title}
        </h2>

        <ListingAddress
          address={listing.addressFormatted}
          listingTypeId={listing.listingTypeId}
          showListingType={false}
        />

        <ListingRate rate={listing.rates} showPerMonth={false} />

        {showCheckStatus && (
        <Badge
          color={listingStatusToBadgeColor[listing.addressVerificationStatus]}
        >
          {translatesVerifications(`address-check-${listing.addressVerificationStatus}`)}
        </Badge>
        )}

        {(showMissingData && listing.isDataMissing) && (
        <Badge>{translatesListingCard('complete-listing')}</Badge>
        )}
      </div>
    </>
  );

  return (
    <div className={cx(
      styles.listingCardViewList,
      !isListingPerson && showCheckStatus && styles[`listingCardViewList__${listing.addressVerificationStatus}Status`],
    )}
    >
      {isListingLinkDisabled ? (
        <div className={styles.listingCardViewList_body}>
          {renderListingBody()}
        </div>
      ) : (
        // eslint-disable-next-line canonical/prefer-react-lazy
        <Link
          className={styles.listingCardViewList_body}
          href={link ?? `${ROUTER_LINKS.listings}/${String(listing.id)}`}
        >
          {renderListingBody()}
        </Link>
      )}

      {(showBookmarkButton ?? showListingUser) && (
        <div className={styles.listingCardViewList_side}>
          {showListingUser && (
            <Link
              className={styles.listingCardViewList_user}
              href={`${ROUTER_LINKS.profile}/${String(listing.user.id)}`}
            >
              <Avatar
                imageSrc={listing.user.avatar.pathSmall}
                alt={listing.user.firstName}
                size={26}
                view="circle"
              />
            </Link>
          )}

          {showBookmarkButton && (
            <button
              className={styles.listingCardViewList_bookmark}
              aria-label="favorite"
              onClick={handleBookmarkClick}
            >
              <FaHeart
                className={cx(styles.listingCardViewList_bookmarkIcon, isListingBookmarked && styles.listingCardViewList_bookmarkIcon__isBookmarked)}
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
